import { CustomTabPanel } from 'views/Production/CustomPanel/index';
import { useSearchParams } from 'react-router-dom';
import FuelUsageGridAnalytics from '../components/FuelUsageGridAnalytics';
import { type fuelUsageFleetData } from 'types/fuelAnalytics';
import { type FuelAnalyticsSpecificLoadingStatesInterface } from 'store/fuelAnalytics.slice';
import FuelUsageDateRangeDiv from '../components/FuelUsageDateRangeDiv';
import { Box, Grid } from '@mui/material';
import FuelUsageBreakdown from '../components/FuelUsageBreakdown';
import { useMemo } from 'react';
import DetailedAnalysis from '../components/DetailedAnalysis';
import useShiftConfig from 'hooks/useShiftConfig';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  value: number;
  index: number;
  enableDarkTheme: boolean;
  isSelected: string;
  setIsSelected: any;
  handleDateChange: any;
  startDate: any;
  setStartDate: any;
  endDate: any;
  setEndDate: any;
  yesterday: any;
  unitSystem: string;
  customerCode: string;
  loadingStates: FuelAnalyticsSpecificLoadingStatesInterface;
  data: fuelUsageFleetData;
  aggregation: string;
  setAggregation: any;
  isPrinting: boolean;
  utilizationType: string;
  setUtilizationType: any;
  setDetailsVal: any;
  setAnalyticsData: any;
  setHoursBreakDown: any;
  setUserSelections: any;
  setSuggestionData: any;
  setFuelConsumption: any;
  fuelEventsPieData: Record<string, number>;
  assetType: string;
  handleFetchCustomDateRangeData: ({
    aggregation,
  }: {
    aggregation: string;
  }) => void;
  handleFetchCustomOnAggregationChange: ({
    aggregation,
    startDate,
    endDate,
  }: {
    aggregation: string;
    startDate: Date;
    endDate: Date;
  }) => void;
}

const AssetTypeFuelUsage = ({
  fuelEventsPieData,
  value,
  index,
  enableDarkTheme,
  unitSystem,
  customerCode,
  isSelected,
  setIsSelected,
  handleDateChange,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  yesterday,
  loadingStates,
  data,
  aggregation,
  setAggregation,
  isPrinting,
  utilizationType,
  setUtilizationType,
  setDetailsVal,
  setAnalyticsData,
  setHoursBreakDown,
  setUserSelections,
  setSuggestionData,
  setFuelConsumption,
  assetType,
  handleFetchCustomDateRangeData,
  handleFetchCustomOnAggregationChange,
}: Props) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMultiShift } = useShiftConfig();
  const shiftWiseAnalysis = useMemo(() => {
    const swParam = searchParams.get('shiftWise');
    if (swParam === 'true') return true;
    if (swParam === 'false') return false;
    return isMultiShift;
  }, [searchParams, isMultiShift]);
  const shiftNumber = parseInt(searchParams.get('shift') ?? '1', 10);

  const setShiftWiseAnalysis = (val: boolean) => {
    setSearchParams((prev) => {
      prev.set('shiftWise', String(val));
      return prev;
    });
  };
  const setShiftNumber = (val: number) => {
    setSearchParams((prev) => {
      prev.set('shift', String(val));
      return prev;
    });
  };

  const assetTypeKey = useMemo(() => {
    return value === 0 ? 'fleet' : value === 1 ? 'hauler' : 'loader';
  }, [value]);

  const excessFuelUsage = useMemo(() => {
    return Object.values(fuelEventsPieData).reduce(
      (acc, curr) => acc + curr,
      0
    );
  }, [fuelEventsPieData]);

  return (
    <>
      <CustomTabPanel value={value} index={index}>
        <>
          <FuelUsageDateRangeDiv
            isSelected={isSelected}
            setIsSelected={setIsSelected}
            handleDateChange={handleDateChange}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            yesterday={yesterday}
            ignoreYtd={false}
            aggregation={aggregation}
            setAggregation={setAggregation}
            shiftWiseAnalysis={shiftWiseAnalysis}
            setShiftWiseAnalysis={setShiftWiseAnalysis}
            shiftNumber={shiftNumber}
            setShiftNumber={setShiftNumber}
            handleFetchCustomDateRangeData={handleFetchCustomDateRangeData}
            handleFetchCustomOnAggregationChange={
              handleFetchCustomOnAggregationChange
            }
          />

          <FuelUsageGridAnalytics
            enableDarkTheme={enableDarkTheme}
            unitSystem={unitSystem}
            isLoading={loadingStates.isLoadingFleetSummary}
            isSelected={isSelected}
            summary={data.summary}
            excessFuelUsage={excessFuelUsage}
            // score={data.score}
            data={data?.summaryChart?.[assetTypeKey]}
            customerCode={customerCode}
            aggregation={aggregation}
            shiftNumber={shiftNumber}
            shiftWiseAnalysis={shiftWiseAnalysis}
            utilizationType={utilizationType}
            setAnalyticsData={setAnalyticsData}
            assetTypeKey={assetTypeKey}
          />

          <Grid
            container
            spacing={2}
            justifyContent="space-between"
            sx={{
              paddingTop: '15px',
              color: palette.textPrimary,
            }}
          >
            <Grid item lg={12} sm={12} md={12}>
              <Grid
                container
                // spacing={2}
                justifyContent="space-between"
              >
                <FuelUsageBreakdown
                  enableDarkTheme={enableDarkTheme}
                  title={t('fuel_usage_breakdown_title', { assetType })}
                  isLoading={
                    loadingStates.isLoadingFleetSummary ||
                    loadingStates.loadingFuelAnalyticsWRTDriverEvents
                  }
                  customerCode={customerCode}
                  data={fuelEventsPieData}
                  setHoursBreakDown={setHoursBreakDown}
                  totalFuelConsumption={
                    data?.summary?.[`${assetTypeKey}_total_fuel_consumption_l`]
                  }
                  assetTypeKey={assetTypeKey}
                />
              </Grid>
            </Grid>
          </Grid>
          <Box
            sx={{
              marginTop: '20px',
              padding: 2,
              backgroundColor: enableDarkTheme
                ? palette.boxBackground
                : palette.boxBackground,
              borderRadius: '5px',
              border: `1px solid ${palette.cardBorderColor}`,
            }}
          >
            <DetailedAnalysis
              assetTypeKey={assetTypeKey}
              aggregation={aggregation}
              summary={data?.summary}
            />
          </Box>
        </>
      </CustomTabPanel>
    </>
  );
};

export default AssetTypeFuelUsage;
